import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Button } from "../../../design-system";
import MediaProjectsView from "./MediaProjectsView";
import { VideoLibraryItem } from "../../../types/Community";
import {
  listenToVideoLibrary,
  uploadVideoLibraryItem,
  removeVideoLibraryItem,
  renameVideoLibraryItem,
  setVideoLibraryItemTags,
  setVideoLibraryItemFolder,
} from "../../../database/community/videoLibrary";

// Folder filter sentinels, matching the existing Free Reel library:
// null = All items, "" = Unsorted (items with no folder).
const FOLDER_ALL = null;
const FOLDER_UNSORTED = "";
const NEW_FOLDER_OPTION = "__new_folder__";

type MediaLibraryProps = {
  communityId?: string;
  initialFolder?: string | null;
};

type UploadingItem = {
  tempId: string;
  name: string;
  pct: number;
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const isAcceptedFile = (file: File): boolean =>
  file.type.startsWith("image/") || file.type.startsWith("video/");

const formatBytes = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
};

const formatDuration = (sec?: number): string => {
  if (!sec || sec <= 0) return "";
  const total = Math.round(sec);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Title = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Subtitle = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  max-width: 64ch;
  line-height: var(--line-height-base);
`;

const Segmented = styled.div`
  display: inline-flex;
  align-self: flex-start;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-pill);
  padding: 3px;
`;

const SegmentButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  background: ${(p) => (p.$active ? "var(--color-surface)" : "transparent")};
  color: ${(p) =>
    p.$active ? "var(--color-text)" : "var(--color-text-secondary)"};
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: ${(p) =>
    p.$active ? "var(--font-weight-semibold)" : "var(--font-weight-medium)"};
  padding: 5px var(--space-3);
  border-radius: var(--radius-pill);
  cursor: pointer;
  box-shadow: ${(p) => (p.$active ? "var(--shadow-xs)" : "none")};
`;

const FolderBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const FolderChip = styled.button<{ $active: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  border: 1px solid
    ${(p) => (p.$active ? "var(--color-accent)" : "var(--color-border-subtle)")};
  background: ${(p) =>
    p.$active ? "var(--color-accent-subtle)" : "var(--color-surface)"};
  color: ${(p) =>
    p.$active ? "var(--color-accent-text)" : "var(--color-text-secondary)"};
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: ${(p) =>
    p.$active ? "var(--font-weight-semibold)" : "var(--font-weight-medium)"};
  cursor: pointer;
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    background var(--duration-fast) var(--ease-standard);

  &:hover {
    border-color: var(--color-border-strong);
  }
`;

const FolderCount = styled.span`
  font-family: var(--font-mono);
  font-size: 10px;
  opacity: 0.7;
`;

const FolderSelect = styled.select`
  appearance: none;
  max-width: 100%;
  height: 24px;
  padding: 0 var(--space-2);
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface-subtle);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: 10px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const DropZone = styled.div<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: var(--space-6);
  border-radius: var(--radius-lg);
  border: 1.5px dashed
    ${(p) => (p.$active ? "var(--color-accent)" : "var(--color-border-subtle)")};
  background: ${(p) =>
    p.$active ? "var(--color-accent-subtle)" : "var(--color-surface-subtle)"};
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  text-align: center;
  cursor: pointer;
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    background var(--duration-fast) var(--ease-standard);
`;

const DropTitle = styled.div`
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const DropHint = styled.div`
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const HiddenInput = styled.input.attrs({
  type: "file",
  accept: "image/*,video/*",
  multiple: true,
})`
  display: none;
`;

const ErrorMsg = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-danger);
`;

const UploadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const ProgressTrack = styled.div`
  position: relative;
  flex: 1;
  height: 6px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-subtle);
  overflow: hidden;
`;

const ProgressBar = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: var(--color-accent);
  transition: width var(--duration-fast) var(--ease-standard);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--space-3);
`;

const AssetCardRoot = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  overflow: hidden;
`;

const Thumb = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: var(--color-surface-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ThumbBadges = styled.div`
  position: absolute;
  left: 6px;
  bottom: 6px;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: var(--radius-pill);
  background: rgba(0, 0, 0, 0.62);
  color: #fff;
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.02em;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  appearance: none;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--color-danger);
  }
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--space-2) var(--space-3) var(--space-3);
`;

const NameInput = styled.input`
  appearance: none;
  width: 100%;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: transparent;
  padding: 2px 4px;
  margin: -2px -4px;
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);

  &:hover {
    border-color: var(--color-border-subtle);
  }
  &:focus {
    outline: none;
    border-color: var(--color-accent);
    background: var(--color-surface);
  }
`;

const Meta = styled.div`
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-tertiary);
`;

const Description = styled.div`
  font-family: var(--font-sans);
  font-size: 11px;
  line-height: 1.35;
  color: var(--color-text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const TagChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 4px 1px 7px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: 10px;
`;

const TagRemove = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  padding: 0;

  &:hover {
    color: var(--color-danger);
  }
`;

const TagInput = styled.input`
  appearance: none;
  flex: 1;
  min-width: 56px;
  border: none;
  background: transparent;
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: 10px;
  padding: 1px 2px;

  &:focus {
    outline: none;
  }
  &::placeholder {
    color: var(--color-text-tertiary);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: var(--space-8) var(--space-4);
  text-align: center;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
`;

// ─── Asset card ───────────────────────────────────────────────────────────

type AssetCardProps = {
  item: VideoLibraryItem;
  folders: string[];
  onRename: (item: VideoLibraryItem, name: string) => void;
  onSetTags: (item: VideoLibraryItem, tags: string[]) => void;
  onSetFolder: (item: VideoLibraryItem, folder: string | null) => void;
  onDelete: (item: VideoLibraryItem) => void;
};

const AssetCard: React.FC<AssetCardProps> = ({
  item,
  folders,
  onRename,
  onSetTags,
  onSetFolder,
  onDelete,
}) => {
  const [name, setName] = useState(item.name || "");
  const [draftTag, setDraftTag] = useState("");

  useEffect(() => {
    setName(item.name || "");
  }, [item.name]);

  const commitName = () => {
    const next = name.trim();
    if (!next || next === item.name) return;
    onRename(item, next);
  };

  const addTag = () => {
    const clean = draftTag.trim().toLowerCase();
    setDraftTag("");
    if (!clean) return;
    const current = item.tags || [];
    if (current.includes(clean)) return;
    onSetTags(item, [...current, clean]);
  };

  const removeTag = (tag: string) =>
    onSetTags(item, (item.tags || []).filter((t) => t !== tag));

  const metaParts = [formatBytes(item.sizeBytes)].filter(Boolean);

  return (
    <AssetCardRoot>
      <Thumb>
        {item.mediaThumbnail || item.mediaUrl ? (
          <img
            src={item.mediaThumbnail || item.mediaUrl}
            alt={item.name}
            loading="lazy"
          />
        ) : null}
        <DeleteButton
          type="button"
          aria-label="Delete asset"
          onClick={() => onDelete(item)}
        >
          ×
        </DeleteButton>
        <ThumbBadges>
          <Badge>{item.type === "video" ? "VIDEO" : "IMAGE"}</Badge>
          {item.type === "video" && item.durationSec ? (
            <Badge>{formatDuration(item.durationSec)}</Badge>
          ) : null}
        </ThumbBadges>
      </Thumb>
      <CardBody>
        <NameInput
          value={name}
          placeholder="Untitled"
          onChange={(e) => setName(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
        />
        {item.description && <Description>{item.description}</Description>}
        {metaParts.length > 0 && <Meta>{metaParts.join(" · ")}</Meta>}
        <FolderSelect
          value={item.folder || FOLDER_UNSORTED}
          onChange={(e) => {
            const value = e.target.value;
            if (value === NEW_FOLDER_OPTION) {
              const next = window.prompt(`New folder for "${item.name}"`, "");
              if (next && next.trim()) onSetFolder(item, next.trim());
              return;
            }
            onSetFolder(item, value === FOLDER_UNSORTED ? null : value);
          }}
        >
          <option value={FOLDER_UNSORTED}>📁 Unsorted</option>
          {folders.map((f) => (
            <option key={f} value={f}>
              📁 {f}
            </option>
          ))}
          <option value={NEW_FOLDER_OPTION}>＋ New folder…</option>
        </FolderSelect>
        <TagRow>
          {(item.tags || []).map((tag) => (
            <TagChip key={tag}>
              {tag}
              <TagRemove
                type="button"
                aria-label={`Remove ${tag}`}
                onClick={() => removeTag(tag)}
              >
                ×
              </TagRemove>
            </TagChip>
          ))}
          <TagInput
            value={draftTag}
            placeholder="+ tag"
            onChange={(e) => setDraftTag(e.target.value)}
            onBlur={addTag}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                addTag();
              }
            }}
          />
        </TagRow>
      </CardBody>
    </AssetCardRoot>
  );
};

// ─── Library ────────────────────────────────────────────────────────────────

export const MediaLibrary: React.FC<MediaLibraryProps> = ({ communityId, initialFolder = FOLDER_ALL }) => {
  const [items, setItems] = useState<VideoLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<UploadingItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState<"library" | "projects">("library");
  // null = All, "" = Unsorted, else a folder name.
  const [activeFolder, setActiveFolder] = useState<string | null>(initialFolder);
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadSeq = useRef(0);

  useEffect(() => {
    if (!communityId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = listenToVideoLibrary(communityId, (next) => {
      setItems(next);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [communityId]);

  const sorted = useMemo(
    () =>
      [...items].sort((a, b) =>
        (b.createdAt || "").localeCompare(a.createdAt || "")
      ),
    [items]
  );

  // Distinct folders (sorted) and how many items each holds.
  const { folders, folderCounts, unsortedCount } = useMemo(() => {
    const counts = new Map<string, number>();
    let unsorted = 0;
    items.forEach((item) => {
      const f = (item.folder || "").trim();
      if (!f) unsorted += 1;
      else counts.set(f, (counts.get(f) || 0) + 1);
    });
    return {
      folders: Array.from(counts.keys()).sort((a, b) => a.localeCompare(b)),
      folderCounts: counts,
      unsortedCount: unsorted,
    };
  }, [items]);

  // Apply the active folder filter to the sorted list.
  const visible = useMemo(() => {
    if (activeFolder === FOLDER_ALL) return sorted;
    if (activeFolder === FOLDER_UNSORTED)
      return sorted.filter((a) => !(a.folder || "").trim());
    return sorted.filter((a) => (a.folder || "").trim() === activeFolder);
  }, [sorted, activeFolder]);

  // Uploads land in the active folder (unless viewing All/Unsorted).
  const uploadTargetFolder =
    activeFolder && activeFolder.trim() ? activeFolder : undefined;

  const handleFiles = async (fileList: FileList | File[] | null) => {
    if (!communityId || !fileList) return;
    const files = Array.from(fileList);
    const accepted = files.filter(isAcceptedFile);
    const rejected = files.length - accepted.length;
    setError(
      rejected > 0
        ? `Skipped ${rejected} unsupported file${rejected === 1 ? "" : "s"}.`
        : ""
    );

    for (const file of accepted) {
      const tempId = `up-${uploadSeq.current++}`;
      setUploading((prev) => [...prev, { tempId, name: file.name, pct: 0 }]);
      try {
        await uploadVideoLibraryItem(communityId, file, {
          folder: uploadTargetFolder,
          onProgress: (pct) =>
            setUploading((prev) =>
              prev.map((u) =>
                u.tempId === tempId ? { ...u, pct: Math.round(pct) } : u
              )
            ),
        });
        // The listenToVideoLibrary subscription surfaces the new item.
      } catch (e: any) {
        console.error("Upload failed:", e);
        setError(e?.message || `Failed to upload ${file.name}.`);
      } finally {
        setUploading((prev) => prev.filter((u) => u.tempId !== tempId));
      }
    }
  };

  const handleRename = async (item: VideoLibraryItem, name: string) => {
    if (!communityId) return;
    try {
      await renameVideoLibraryItem(communityId, item, name);
    } catch (e: any) {
      console.error("Failed to rename asset:", e);
      setError(e?.message || "Failed to rename.");
    }
  };

  const handleSetTags = async (item: VideoLibraryItem, tags: string[]) => {
    if (!communityId) return;
    // Optimistic; the snapshot confirms.
    setItems((prev) =>
      prev.map((a) => (a.id === item.id ? { ...a, tags } : a))
    );
    try {
      await setVideoLibraryItemTags(communityId, item, tags);
    } catch (e: any) {
      console.error("Failed to set tags:", e);
      setError(e?.message || "Failed to save tags.");
    }
  };

  const handleSetFolder = async (
    item: VideoLibraryItem,
    folder: string | null
  ) => {
    if (!communityId) return;
    try {
      await setVideoLibraryItemFolder(communityId, item, folder);
    } catch (e: any) {
      console.error("Failed to set folder:", e);
      setError(e?.message || "Failed to move to folder.");
    }
  };

  const handleNewFolder = () => {
    const next = window.prompt("New folder name", "");
    if (next && next.trim()) setActiveFolder(next.trim());
  };

  const handleDelete = async (item: VideoLibraryItem) => {
    if (!communityId) return;
    setItems((prev) => prev.filter((a) => a.id !== item.id));
    try {
      await removeVideoLibraryItem(communityId, item);
    } catch (e: any) {
      console.error("Failed to delete asset:", e);
      setError(e?.message || "Failed to delete asset.");
    }
  };

  return (
    <Root>
      <HeaderRow>
        <HeaderText>
          <Title>Media Library</Title>
          <Subtitle>
            The community's shared pool of clips and images. Upload here, tag
            them, and the MCP composes them into reels and carousels. This is
            the same library the reel generators draw from.
          </Subtitle>
        </HeaderText>
        {view === "library" && (
          <Button
            variant="primary"
            size="sm"
            disabled={!communityId}
            onClick={() => inputRef.current?.click()}
          >
            Upload media
          </Button>
        )}
      </HeaderRow>

      <Segmented role="tablist">
        <SegmentButton
          type="button"
          $active={view === "library"}
          onClick={() => setView("library")}
        >
          Library
        </SegmentButton>
        <SegmentButton
          type="button"
          $active={view === "projects"}
          onClick={() => setView("projects")}
        >
          Projects
        </SegmentButton>
      </Segmented>

      {view === "projects" ? (
        <MediaProjectsView communityId={communityId} />
      ) : (
        <>
      <DropZone
        $active={dragActive}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!dragActive) setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <DropTitle>Drop images and videos here</DropTitle>
        <DropHint>
          or click to browse · multiple files supported ·{" "}
          {uploadTargetFolder
            ? `uploads go to "${uploadTargetFolder}"`
            : "uploads go to Unsorted"}
        </DropHint>
      </DropZone>

      <HiddenInput
        ref={inputRef}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {error && <ErrorMsg>{error}</ErrorMsg>}

      {uploading.length > 0 && (
        <UploadList>
          {uploading.map((u) => (
            <UploadRow key={u.tempId}>
              <span
                style={{
                  minWidth: 0,
                  flex: "0 1 200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {u.name}
              </span>
              <ProgressTrack>
                <ProgressBar style={{ width: `${u.pct}%` }} />
              </ProgressTrack>
              <span>{u.pct}%</span>
            </UploadRow>
          ))}
        </UploadList>
      )}

      {!loading && items.length > 0 && (
        <FolderBar>
          <FolderChip
            type="button"
            $active={activeFolder === FOLDER_ALL}
            onClick={() => setActiveFolder(FOLDER_ALL)}
          >
            All <FolderCount>{items.length}</FolderCount>
          </FolderChip>
          <FolderChip
            type="button"
            $active={activeFolder === FOLDER_UNSORTED}
            onClick={() => setActiveFolder(FOLDER_UNSORTED)}
          >
            Unsorted <FolderCount>{unsortedCount}</FolderCount>
          </FolderChip>
          {folders.map((f) => (
            <FolderChip
              type="button"
              key={f}
              $active={activeFolder === f}
              onClick={() => setActiveFolder(f)}
            >
              📁 {f} <FolderCount>{folderCounts.get(f) || 0}</FolderCount>
            </FolderChip>
          ))}
          <FolderChip type="button" $active={false} onClick={handleNewFolder}>
            ＋ New folder
          </FolderChip>
        </FolderBar>
      )}

      {loading ? (
        <EmptyState>Loading library…</EmptyState>
      ) : items.length === 0 && uploading.length === 0 ? (
        <EmptyState>
          No media yet. Upload clips and images to start building reels and
          carousels.
        </EmptyState>
      ) : visible.length === 0 ? (
        <EmptyState>
          Nothing in this folder yet. Uploads here go to{" "}
          {uploadTargetFolder ? `"${uploadTargetFolder}"` : "Unsorted"}.
        </EmptyState>
      ) : (
        <Grid>
          {visible.map((item) => (
            <AssetCard
              key={item.id}
              item={item}
              folders={folders}
              onRename={handleRename}
              onSetTags={handleSetTags}
              onSetFolder={handleSetFolder}
              onDelete={handleDelete}
            />
          ))}
        </Grid>
      )}
        </>
      )}
    </Root>
  );
};

export default MediaLibrary;
